type StatusMapType = {
  label: string;
  color: string;
  dotColor: string;
};

export type OrderStatusType =
  | 'PAID'
  | 'CONFIRMED'
  | 'PENDING_CONFIRMATION'
  | 'PENDING_PAYMENT'
  | 'PROCESSING'
  | 'PARTIALLY_SHIPPED'
  | 'PARTIALLY_IN_WAREHOUSE'
  | 'SHIPPED'
  | 'IN_WAREHOUSE'
  | 'REJECTED';

export type OrderItemStatusType =
  | 'PENDING'
  | 'AWAITING_RELATED_ITEMS'
  | 'READY_TO_ORDER'
  | 'PRICE_REQUESTED'
  | 'PROCESSING'
  | 'ORDERED_FROM_SUPPLIER'
  | 'CONFIRMED_BY_SUPPLIER'
  | 'PAYMENT_PROCESSING'
  | 'PAID_TO_SUPPLIER'
  | 'SHIPPED'
  | 'IN_WAREHOUSE'
  | 'REJECTED';

type StatusType = {
  value: OrderStatusType;
  label: string;
};

export const OrderStatusTypes: StatusType[] = [
  {
    value: 'PROCESSING',
    label: 'Processing',
  },
  {
    value: 'CONFIRMED',
    label: 'Confirmed',
  },
  {
    value: 'PAID',
    label: 'Paid',
  },
  {
    value: 'PENDING_PAYMENT',
    label: 'Pending Payment',
  },
  {
    value: 'PENDING_CONFIRMATION',
    label: 'Pending Confirmation',
  },
  {
    value: 'PARTIALLY_SHIPPED',
    label: 'Partially Shipped',
  },
  {
    value: 'PARTIALLY_IN_WAREHOUSE',
    label: 'Partially In Warehouse',
  },
  {
    value: 'SHIPPED',
    label: 'Shipped',
  },
  {
    value: 'IN_WAREHOUSE',
    label: 'In Warehouse',
  },
  {
    value: 'REJECTED',
    label: 'Rejected',
  },
];

export const ORDER_STATUS_MAPPING: Record<OrderStatusType, StatusMapType> = {
  CONFIRMED: {
    label: 'Confirmed',
    color: '#E0FAEC',
    dotColor: 'green',
  },
  PAID: {
    label: 'Paid',
    color: '#E0FAEC',
    dotColor: 'green',
  },
  PENDING_CONFIRMATION: {
    label: 'Pending Confirmation',
    color: '#FFF7E6',
    dotColor: '#fa8c16',
  },
  PENDING_PAYMENT: {
    label: 'Pending Payment',
    color: '#FFF7E6',
    dotColor: '#fa8c16',
  },
  PROCESSING: {
    label: 'Processing',
    color: '#E6F0FF',
    dotColor: '#0D6EFD',
  },
  PARTIALLY_SHIPPED: {
    label: 'Partially Shipped',
    color: '#EBDFFF',
    dotColor: '#B86DFF',
  },
  PARTIALLY_IN_WAREHOUSE: {
    label: 'Partially In Warehouse',
    color: '#E6FCFF',
    dotColor: '#00B8D3',
  },
  SHIPPED: {
    label: 'Shipped',
    color: '#EEEFFF',
    dotColor: '#5865F2',
  },
  IN_WAREHOUSE: {
    label: 'In Warehouse',
    color: '#E5F4FF',
    dotColor: '#15C1FF',
  },
  REJECTED: {
    label: 'Rejected',
    color: '#fff2f0',
    dotColor: 'red',
  },
};

export const ORDER_ITEM_STATUS_MAPPING: Record<OrderItemStatusType, StatusMapType> = {
  PENDING: {
    label: 'Pending',
    color: '#FFF7E6',
    dotColor: '#fa8c16',
  },
  REJECTED: {
    label: 'Rejected',
    color: '#fff2f0',
    dotColor: 'red',
  },
  PROCESSING: {
    label: 'Processing',
    color: '#E6F0FF',
    dotColor: '#0D6EFD',
  },
  READY_TO_ORDER: {
    label: 'Ready To Order',
    color: '#E3F4EE',
    dotColor: '#05796B',
  },
  PRICE_REQUESTED: {
    label: 'Price Requested',
    color: '#FFF1EB',
    dotColor: '#FF9865',
  },
  PAYMENT_PROCESSING: {
    label: 'Payment Processing',
    color: '#FFEEE0',
    dotColor: '#FF9865',
  },
  SHIPPED: {
    label: 'Shipped',
    color: '#EEEFFF',
    dotColor: '#5865F2',
  },
  IN_WAREHOUSE: {
    label: 'In Warehouse',
    color: '#E5F4FF',
    dotColor: '#15C1FF',
  },
  ORDERED_FROM_SUPPLIER: {
    label: 'Ordered From Supplier',
    color: '#FEE0FF',
    dotColor: '#FF47E0',
  },
  CONFIRMED_BY_SUPPLIER: {
    label: 'Confirmed By Supplier',
    color: '#DDE8E6',
    dotColor: '#05796B',
  },
  PAID_TO_SUPPLIER: {
    label: 'Paid To Supplier',
    color: '#E0FAE1',
    dotColor: '#44AE6F',
  },
  AWAITING_RELATED_ITEMS: {
    label: 'Awaiting Related Items',
    color: '#FFDCCB',
    dotColor: '#FE6D25',
  },
};

export interface OrderListArgument {
  page?: number;
  size?: number;
  sort?: string;
  sortField?: string;
  sortOrder?: string;
  search?: string;
  countryId?: string;
  statusId?: string;
  clientId?: string;
  region?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  lastUpdatedDateFrom?: string;
  lastUpdatedDateTo?: string;
  filters?: OrderFilterParams;
  forDashboard?: boolean;
}

export interface OrderFilterParams {
  countryId?: string;
  status?: string[];
  clientId?: string;
  region?: string;
  createdDate?: [Date, Date];
  lastUpdatedDate?: [Date, Date];
}

// Define allowed status transitions
export const AllowedStatusTransitions: Record<OrderStatusType, OrderStatusType[]> = {
  PENDING_PAYMENT: ['PAID', 'REJECTED'],
  PENDING_CONFIRMATION: ['CONFIRMED', 'REJECTED'],
  CONFIRMED: [],
  PAID: [],
  REJECTED: [],
  PROCESSING: [],
  PARTIALLY_SHIPPED: [],
  SHIPPED: [],
  IN_WAREHOUSE: [],
  PARTIALLY_IN_WAREHOUSE: [],
};

type AssignmentRolesKeys =
  | 'administrator'
  | 'salesManager'
  | 'salesManagerAssistant'
  | 'purchaseManager'
  | 'purchaseManagerAssistant'
  | 'postSalesManager'
  | 'postSalesManagerAssistant';

export const AssignmentRoleMapping: Record<AssignmentRolesKeys, string> = {
  administrator: 'Administrator',
  salesManager: 'Sales Manager',
  salesManagerAssistant: 'SM Assistant',
  purchaseManager: 'Purchase Manager',
  purchaseManagerAssistant: 'PM Assistant',
  postSalesManager: 'Post-Sales Manager',
  postSalesManagerAssistant: 'Post-SM Assistant',
};

export const OrderCancellationReasons = {
  CLIENT_REQUEST: { label: 'Client request', value: 'CLIENT_REQUEST' },
  SUPPLIER_UNAVAILABLE: { label: 'Supplier unavailable', value: 'SUPPLIER_UNAVAILABLE' },
  PRICE_CHANGE: { label: 'Price change', value: 'PRICE_CHANGE' },
  DELIVERY_DELAY: { label: 'Delivery delay', value: 'DELIVERY_DELAY' },
  QUALITY_ISSUES: { label: 'Quality issues', value: 'QUALITY_ISSUES' },
  OTHER: { label: 'Other', value: 'OTHER' },
};

export const OrderRejectionReasons = {
  OUT_OF_SCOPE: { label: 'Out of scope', value: 'OUT_OF_SCOPE' },
  NO_LONGER_NEEDED: { label: 'No longer needed', value: 'NO_LONGER_NEEDED' },
  OTHER: { label: 'Other', value: 'OTHER' },
};

type Order = {
  id: string;
  uuid: string | number;
  client: {
    id: string;
    name: string;
    verified: boolean;
  };
  deliveryCountry: {
    id: string;
    name: string;
    fileLocation?: string;
    regions?: any;
  };
  createdBy: string;
  createdDate: string;
  lastUpdated: string;
  status: string;
  formattedCreatedDate?: {
    full: string;
    day: string;
  };
  formattedLastUpdated?: {
    full: string;
    day: string;
  };
};

export type { Order, AssignmentRolesKeys };
